import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  LayoutGrid,
  FolderOpen,
  FileText,
  Activity,
  Users,
  ChevronDown,
  ChevronRight,
  Pencil,
} from "lucide-react";
import { apiClient } from "../../utils/apiClient/index";
import { useAlert } from "../../hooks/useAlert";
import type { Case } from "../../types";
import Breadcrumb from "../../components/common/common/Breadcrumb";
import Tabs, { type Tab } from "../../components/common/ui/tabs/Tabs";
import OverviewTab from "./components/OverviewTab";
import DocumentsTab from "./components/DocumentsTab";
import RequestsTab from "./components/RequestsTab";
import ActivityTab from "./components/ActivityTab";
import AssignmentsTab from "./components/AssignmentsTab";
import { CaseStatusBadge, CasePriorityBadge } from "../../utils/caseBadges";
import { useOrg } from "../../contexts/OrgContext";
import Loading from "../../components/common/ui/loading";

export const CaseDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const [searchParams] = useSearchParams();
  const { setAlert } = useAlert();

  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(false);
  const [fileCount, setFileCount] = useState(0);
  const [formSubmissionCount, setFormSubmissionCount] = useState(0);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [lastFileUpload, setLastFileUpload] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const { navigateOrgUrl, orgId } = useOrg();

  // Support both /cases/:caseId and /cases?id=:caseId routes
  const effectiveCaseId = caseId || searchParams.get("id");

  useEffect(() => {
    if (effectiveCaseId) {
      loadCaseData();
    }
  }, [effectiveCaseId]);

  useEffect(() => {
    if (effectiveCaseId && caseData) {
      loadCaseStats();
    }
  }, [effectiveCaseId, caseData?.id]);

  const loadCaseData = async () => {
    if (!effectiveCaseId) return;

    setLoading(true);
    try {
      const data = await apiClient.getCase(effectiveCaseId);
      setCaseData(data);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to load case details",
      });
      navigate("/cases");
    } finally {
      setLoading(false);
    }
  };

  const loadCaseStats = async () => {
    if (!effectiveCaseId || !caseData?.organizationId) return;

    try {
      // Load file count
      const filesResponse = await apiClient.listFilesByCase(effectiveCaseId);
      setFileCount((filesResponse as any[])?.length || 0);

      // Load form submissions count
      const submissions = await apiClient.listSubmissions(
        caseData.organizationId,
        effectiveCaseId,
      );
      setFormSubmissionCount(submissions.length);

      const requests = await apiClient.getRequestsByCase(effectiveCaseId);
      const pendingCount = requests.filter(
        (cf) => cf.status === "pending" || cf.status === "sent",
      ).length;
      setPendingRequestCount(pendingCount);

      // Load last file upload from audit logs
      const auditResponse = await apiClient.getAuditLogs({
        caseId: effectiveCaseId,
        action: "file_upload",
        limit: 1,
        page: 1,
      });
      setLastFileUpload(auditResponse.logs[0]?.createdAt || null);
    } catch (error) {
      console.error("Failed to load case stats:", error);
    }
  };

  if (loading) {
    return <Loading text={"Loading case..."} />;
  }

  if (!caseData || !effectiveCaseId) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">Case not found</p>
        </div>
      </div>
    );
  }

  const handleSwitchToActivityTab = () => {
    setActiveTab("activity");
  };

  const tabs: Tab[] = [
    {
      id: "overview",
      label: "Overview",
      icon: <LayoutGrid size={18} />,
      content: (
        <OverviewTab
          caseData={caseData}
          fileCount={fileCount}
          formSubmissionCount={formSubmissionCount}
          pendingRequestCount={pendingRequestCount}
          lastFileUpload={lastFileUpload}
          caseId={effectiveCaseId}
          onSwitchToActivityTab={handleSwitchToActivityTab}
        />
      ),
    },
    {
      id: "documents",
      label: "Records",
      icon: <FolderOpen size={18} />,
      content: <DocumentsTab caseId={effectiveCaseId} orgId={orgId || ""} />,
    },
    {
      id: "requests",
      label: "Requests",
      icon: <FileText size={18} />,
      content: <RequestsTab caseId={effectiveCaseId} />,
    },
    {
      id: "assignments",
      label: "Access",
      icon: <Users size={18} />,
      content: (
        <AssignmentsTab
          caseId={effectiveCaseId}
          organizationId={caseData.organizationId}
          onAssignmentChange={loadCaseStats}
        />
      ),
    },
    {
      id: "activity",
      label: "Activity",
      icon: <Activity size={18} />,
      content: <ActivityTab caseId={effectiveCaseId} />,
    },
  ];

  return (
    <div className="space-y-1">
      <Breadcrumb
        items={[
          { label: "Cases", path: navigateOrgUrl("/cases") },
          { label: caseData.title || `Case ${caseData.id}` },
        ]}
      />
      {/* Case Header with Edit Button */}
      <div className="rounded-2xl border border-gray-200 bg-white px-6 py-4 dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="flex justify-between">
          <div className="flex items-center">
            {caseData.description && (
              <button
                className="hover:bg-gray-200 rounded-lg p-1 mr-2"
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              >
                {!isDescriptionExpanded ? (
                  <ChevronRight className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </button>
            )}
            <h2 className="text-2xl font-semibold text-black dark:text-white">
              {caseData.title}
            </h2>
          </div>
          <div className="flex gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Status:
              </span>
              <CaseStatusBadge status={caseData.status} />
            </div>
            <div className="text-gray-300 dark:text-gray-700">|</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Priority:
              </span>
              <CasePriorityBadge priority={caseData.priority} />
            </div>
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Case ID: {caseData.id}
        </p>

        {caseData.description && (
          <div className="mt-4">
            {isDescriptionExpanded && (
              <div className="mt-2 rounded-lg bg-gray-50 p-4 dark:bg-gray-900/50">
                <div className="flex justify-apart group">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {caseData.description}
                  </p>
                  <div className="opacity-0 transition-opacity group-hover:opacity-100 ml-1">
                    <button
                      type="button"
                      onClick={() => {
                        console.log("coming soon");
                      }}
                      className="p-0.5 text-gray-400 hover:text-gray-600"
                      title="Edit description"
                    >
                      <Pencil size={12} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        tabs={tabs}
        defaultTab="overview"
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
};
